'use client'

import { AlertTriangle, Info } from 'lucide-react'
import type { CalculationWarnings as Warnings } from '@/lib/types'

interface CalculationWarningsProps {
	warnings: Warnings
}

/** One row in the warnings banner — tone drives the color */
function WarningRow({
	tone,
	title,
	body,
}: {
	tone: 'warning' | 'info'
	title: string
	body: string
}) {
	const Icon = tone === 'warning' ? AlertTriangle : Info
	const toneClass =
		tone === 'warning'
			? 'border-warning/40 bg-warning/10 text-warning'
			: 'border-border bg-surface text-text-secondary'
	return (
		<output className={`flex gap-2 rounded-md border px-3 py-2 text-xs ${toneClass}`}>
			<Icon className="size-3.5 shrink-0 mt-0.5" aria-hidden />
			<div className="space-y-0.5">
				<div className="font-medium">{title}</div>
				<div className="text-[11px] opacity-90">{body}</div>
			</div>
		</output>
	)
}

type Row = { tone: 'warning' | 'info'; title: string; body: string }

/** Build a row (if any) for fuel-price health */
function pricesRow(w: Warnings): Row | null {
	if (w.fuelPricesStatus === 'unavailable') {
		return {
			tone: 'warning',
			title: 'Fuel prices unavailable',
			body: `All states priced using the national average. ${w.fuelPricesReason ?? ''}`.trim(),
		}
	}
	if (w.fuelPricesStatus === 'fallback') {
		return {
			tone: 'warning',
			title: 'Using national average fuel prices',
			body:
				w.fuelPricesReason ??
				'EIA state-level data is temporarily unavailable — totals may be less accurate.',
		}
	}
	// NOTE: Intentionally no banner for `fuelPricesFallbackStates` alone.
	// PADD fallback is the **expected** state for 41/50 states (EIA only
	// publishes 9 states directly), so a banner would fire on almost every
	// multi-state tour — fatigue that devalues the warnings that actually
	// matter. The per-state breakdown tags each price with `state` / `PADD`
	// and carries a legend, which conveys the same information without
	// shouting. The banner still fires on `fallback` (national average
	// everywhere) and `unavailable` (no EIA data at all) above — those are
	// genuine accuracy hits, not routine PADD substitution.
	//
	// Also: no "Fuel prices refreshed Xh ago" info banner. EIA publishes
	// weekly, so any cache age under a week contains genuinely current
	// data. The prior 4-hour threshold made totally-fresh data look
	// stale to users, while the cost-summary's "Prices as of today /
	// yesterday" line and the live ticker already convey freshness
	// correctly. Removed 2026-04-19 — was miscalibrated per our weekly
	// publish cadence.
	return null
}

/** Build a row (if any) for elevation health */
function elevationRow(w: Warnings): Row | null {
	if (w.elevationStatus === 'unavailable') {
		return {
			tone: 'warning',
			title: 'Elevation data unavailable — estimate not grade-adjusted',
			body: `Fuel usage on steep sections may be underestimated. ${w.elevationReason ?? ''}`.trim(),
		}
	}
	if (w.elevationStatus === 'degraded') {
		// Copy intentionally minimal. Mapbox Tilequery (10m contour
		// resolution) produces fuel-cost estimates that are close enough
		// to the Open-Meteo SRTM raster primary at our 5-mile sampling
		// that the operational impact on a full route total is small.
		// The alarming "reduced precision" wording overstated it for a
		// fuel calculator. See docs/AI_BLIND_SPOTS.md on banner
		// calibration.
		return {
			tone: 'info',
			title: 'Elevation via backup source',
			body: 'Within normal accuracy range.',
		}
	}
	if (w.elevationStatus === 'partial') {
		return {
			tone: 'info',
			title: 'Elevation data was partial',
			body: 'Some sections of the route were estimated without grade adjustment.',
		}
	}
	return null
}

/** Build a row (if any) for state-detection health */
function stateDetectionRow(w: Warnings): Row | null {
	if (w.stateDetectionStatus === 'unavailable') {
		return {
			tone: 'warning',
			title: 'State boundaries could not be detected',
			body: 'The entire route was priced using the national average. Retry in a moment for state-level accuracy.',
		}
	}
	if (w.stateDetectionStatus === 'degraded') {
		return {
			tone: 'info',
			title: 'Some miles priced at the national average',
			body:
				w.stateDetectionDegradedMiles > 0
					? `About ${Math.round(w.stateDetectionDegradedMiles)} mi couldn't be matched to a U.S. state — typically stops outside the U.S. (Canada/Mexico) or coastal routing — so those miles use the U.S. national-average fuel price. Accuracy is reduced for that portion.`
					: 'Some miles outside U.S. state boundaries were priced at the national average — accuracy is reduced for that portion.',
		}
	}
	return null
}

/**
 * Build a row (if any) for vehicle-aware routing. Only the fallback case gets
 * a banner — `applied` is the normal state for buses and trucks and would
 * just be noise.
 */
function routingRow(w: Warnings): Row | null {
	if (w.vehicleRestrictions !== 'relaxed') return null
	return {
		tone: 'warning',
		title: 'No route found that fits this vehicle',
		body: "The map couldn't find a route that respects this vehicle's height and weight limits, so this is a standard car route. Check low bridges and weight-restricted roads before you drive it.",
	}
}

/**
 * Renders per-source warnings above the cost summary.
 *
 * Rule of thumb: if any data source degraded the estimate's accuracy, say so.
 * Never let a user read a number without knowing its confidence level.
 */
export function CalculationWarnings({ warnings }: CalculationWarningsProps) {
	const rows: Row[] = [
		routingRow(warnings),
		pricesRow(warnings),
		elevationRow(warnings),
		stateDetectionRow(warnings),
	].filter((r): r is Row => r !== null)

	if (rows.length === 0) return null

	return (
		<div className="space-y-1.5">
			{rows.map((r) => (
				<WarningRow key={r.title} tone={r.tone} title={r.title} body={r.body} />
			))}
		</div>
	)
}
